/**
 * Theme Configuration - Single source of truth for all visual design tokens
 *
 * This file centralizes all theme-related values that were previously scattered
 * across tailwind.config.js, index.css, and individual components.
 *
 * Usage:
 * - Import from this file for programmatic access
 * - Values are also exported to CSS custom properties via ThemeProvider
 * - Tailwind config imports from here for class generation
 */

export interface ColorPalette {
  /** Primary brand color */
  primary: string;
  /** Accent/secondary brand color */
  accent: string;
  /** Background colors (darkest to lightest) */
  background: {
    base: string;
    surface: string;
    elevated: string;
  };
  /** Text colors (brightest to dimmest) */
  text: {
    primary: string;
    secondary: string;
    muted: string;
  };
  /** Semantic colors */
  semantic: {
    error: string;
    success: string;
    warning: string;
  };
}

export interface LayerColors {
  primary: string;
  primaryRgb: string;
  visited: string;
  visitedRgb: string;
  highlighted: string;
}

export interface LayerTheme {
  id: string;
  label: string;
  shortLabel: string;
  colors: LayerColors;
  dashedBorder?: boolean;
}

export interface AnimationDurations {
  instant: number;
  fast: number;
  normal: number;
  slow: number;
  dramatic: number;
}

export interface FontStack {
  sans: string[];
  mono: string[];
}

export interface ThemeConfig {
  colors: ColorPalette;
  layers: LayerTheme[];
  animation: {
    durations: AnimationDurations;
    totalAnimationDuration: number;
    timeMachinePlaybackDuration: number;
    glowPulseDuration: string;
    glowPulseScale: number;
    glowPulseScaleWishlist: number;
  };
  fonts: FontStack;
  map: {
    defaultCenter: [number, number];
    defaultZoom: number;
    minZoom: number;
    maxZoom: number;
    clusterMaxRadius: number;
    clusterDisableAtZoom: number;
  };
  ui: {
    scrollThreshold: number;
    swipeThreshold: number;
    headerHeight: {
      mobile: string;
      desktop: string;
    };
    statsBarHeight: {
      mobile: string;
      desktop: string;
    };
  };
}

/**
 * Default theme configuration
 * This is the "Love Map" dark theme
 */
export const defaultTheme: ThemeConfig = {
  colors: {
    primary: '#8B5CF6',
    accent: '#EC4899',
    background: {
      base: '#0A0A0F',
      surface: '#1A1A24',
      elevated: '#252530',
    },
    text: {
      primary: '#F9FAFB',
      secondary: '#9CA3AF',
      muted: '#6B7280',
    },
    semantic: {
      error: '#EF4444',
      success: '#10B981',
      warning: '#F59E0B',
    },
  },
  layers: [
    {
      id: 'travels',
      label: 'Travels',
      shortLabel: 'Travels',
      colors: {
        primary: '#8B5CF6',
        primaryRgb: '139, 92, 246',
        visited: '#64748B',
        visitedRgb: '100, 116, 139',
        highlighted: '#6D28D9',
      },
    },
    {
      id: 'love-is-blind',
      label: 'Love is Blind',
      shortLabel: 'LiB',
      colors: {
        primary: '#EC4899',
        primaryRgb: '236, 72, 153',
        visited: '#9D7A8C',
        visitedRgb: '157, 122, 140',
        highlighted: '#BE185D',
      },
    },
    {
      id: 'wishlist',
      label: 'Wishlist',
      shortLabel: 'Wish',
      colors: {
        primary: '#10B981',
        primaryRgb: '16, 185, 129',
        visited: '#5F8A7A',
        visitedRgb: '95, 138, 122',
        highlighted: '#059669',
      },
      dashedBorder: true,
    },
  ],
  animation: {
    durations: {
      instant: 100,
      fast: 150,
      normal: 200,
      slow: 300,
      dramatic: 500,
    },
    totalAnimationDuration: 3000,
    timeMachinePlaybackDuration: 12000,
    glowPulseDuration: '1.5s',
    glowPulseScale: 1.25,
    glowPulseScaleWishlist: 1.2,
  },
  fonts: {
    sans: ['Inter', 'system-ui', 'sans-serif'],
    mono: ['ui-monospace', 'SFMono-Regular', 'monospace'],
  },
  map: {
    defaultCenter: [50.0, 25.0],
    defaultZoom: 4,
    minZoom: 2,
    maxZoom: 18,
    clusterMaxRadius: 50,
    clusterDisableAtZoom: 12,
  },
  ui: {
    scrollThreshold: 300,
    swipeThreshold: 50,
    headerHeight: {
      mobile: '6rem', // 96px (includes layer selector row)
      desktop: '4rem', // 64px
    },
    statsBarHeight: {
      mobile: '3.5rem', // 56px
      desktop: '4rem', // 64px
    },
  },
};

/**
 * Get the current theme configuration
 * In the future, this could support runtime theme switching
 */
export function getTheme(): ThemeConfig {
  return defaultTheme;
}

/**
 * Get layer configuration by ID
 */
export function getLayerTheme(layerId: string): LayerTheme | undefined {
  return defaultTheme.layers.find(l => l.id === layerId);
}

/**
 * Convert hex color to RGB string (for CSS custom properties)
 */
export function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
}

/**
 * Generate CSS custom properties from theme config
 */
export function generateCssVariables(theme: ThemeConfig = defaultTheme): Record<string, string> {
  const vars: Record<string, string> = {};

  // Colors
  vars['--color-primary'] = theme.colors.primary;
  vars['--color-accent'] = theme.colors.accent;
  vars['--color-bg-base'] = theme.colors.background.base;
  vars['--color-bg-surface'] = theme.colors.background.surface;
  vars['--color-bg-elevated'] = theme.colors.background.elevated;
  vars['--color-text-primary'] = theme.colors.text.primary;
  vars['--color-text-secondary'] = theme.colors.text.secondary;
  vars['--color-text-muted'] = theme.colors.text.muted;

  // Layer colors
  theme.layers.forEach(layer => {
    const prefix = `--layer-${layer.id}`;
    vars[`${prefix}-primary`] = layer.colors.primary;
    vars[`${prefix}-primary-rgb`] = layer.colors.primaryRgb;
    vars[`${prefix}-visited`] = layer.colors.visited;
    vars[`${prefix}-visited-rgb`] = layer.colors.visitedRgb;
    vars[`${prefix}-highlighted`] = layer.colors.highlighted;
  });

  // Animation
  vars['--duration-instant'] = `${theme.animation.durations.instant}ms`;
  vars['--duration-fast'] = `${theme.animation.durations.fast}ms`;
  vars['--duration-normal'] = `${theme.animation.durations.normal}ms`;
  vars['--duration-slow'] = `${theme.animation.durations.slow}ms`;
  vars['--duration-dramatic'] = `${theme.animation.durations.dramatic}ms`;
  vars['--glow-pulse-duration'] = theme.animation.glowPulseDuration;
  vars['--glow-pulse-scale'] = String(theme.animation.glowPulseScale);
  vars['--glow-pulse-scale-wishlist'] = String(theme.animation.glowPulseScaleWishlist);

  // Fonts
  vars['--font-sans'] = theme.fonts.sans.join(', ');
  vars['--font-mono'] = theme.fonts.mono.join(', ');

  return vars;
}

export default defaultTheme;
